import { useState } from 'react';
import { useQuery } from 'react-query';
import { DefaultTable } from 'Components/DefaultTable';
import { Container } from '../../Components/Container';
import { Information } from 'Components/Information';
import { tableHeader } from './table-header.js';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { PageLoaderList } from 'Components/PageLoader/List';
import { deleteCompany, getCompaniesPaged } from 'services/company-service';
import { useAuthUser } from 'store/auth';
import { useSelector } from 'react-redux';
import { hasModulePermission } from 'utils/permission_utils';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { NoData } from 'Components/NoData';
import { Card, Pagination, Stack, Button } from '@mui/material';
import { Column, Row } from 'Components/Grid';
import { CustomTextField } from 'Components/Form/styles';
import { useForm } from 'react-hook-form';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

export const Companies = () => {
  const user = useSelector(useAuthUser);
  const SwalReact = withReactContent(Swal);
  const [open, setOpen] = useState(false);
  const [reFetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const { register, watch, reset } = useForm({
    defaultValues: {
      name: '',
    },
  });
  const [appliedNameFilter, setAppliedNameFilter] = useState('');
  const {
    isLoading,
    error,
    data: pagedCompanies,
  } = useQuery(['getCompanies', reFetch, page], async () => {
    const result = await getCompaniesPaged(page, 15);
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const hideDelete = () => {
    return !hasModulePermission(user.permissions, ['super_admin_global', 'admin_global']);
  };

  const onDelete = item => {
    setItemToDelete(item);
    setOpen(true);
  };

  const onClose = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const onConfirm = async () => {
    try {
      const result = await deleteCompany(itemToDelete?._id);
      if (result.status === 204) {
        onClose();
        setRefetch(!reFetch);
      } else {
        throw new Error('Ocorreu um erro ao tentar removar a Empresa!');
      }
    } catch (error) {
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const getFilteredCompanies = () => {
    if (!pagedCompanies?.data) return [];
    const term = (appliedNameFilter || '').trim().toLowerCase();
    if (term === '') return pagedCompanies.data;
    return pagedCompanies.data.filter(company => (company.name || '').toLowerCase().includes(term));
  };

  return (
    <Container title="Clientes" breadcrumb="Configurações Gerais / Clientes" showButtonAdd linkPage="/companies">
      <Information
        title={'O que são clientes'}
        description={`Aqui segue explicação prévia.`}
        style={{
          marginTop: '24px',
        }}
      />
      <Card className="mb-2 mt-3" sx={{ padding: '24px' }}>
        <Row className="mb-3" sx={{ alignItems: 'center' }}>
          <Column xs={12} md={6}>
            <CustomTextField
              {...register('name')}
              label="Nome"
              sx={{
                minHeight: '40px',
                maxHeight: '40px',
                margin: '0 auto',
                width: '100%',
                ' > div': {
                  minHeight: '40px',
                },
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Column>
          <Column xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              gap: '12px',
              justifyContent: 'flex-start',
              alignItems: 'center',
              width: '100%'
            }}>
              <Button
                size="small"
                variant="outlined"
                sx={{
                  width: '120px',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  height: '36px',
                  fontWeight: 500
                }}
                onClick={() => {
                  reset();
                  setAppliedNameFilter('');
                  setRefetch(!reFetch);
                }}
              >
                Limpar
              </Button>
              <Button
                size="small"
                variant="contained"
                sx={{
                  width: '120px',
                  borderRadius: '8px',
                  padding: '8px 20px',
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  height: '36px',
                  fontWeight: 500
                }}
                endIcon={<SearchOutlinedIcon />}
                onClick={() => {
                  setAppliedNameFilter((watch('name') || '').trim());
                  setRefetch(!reFetch);
                }}
              >
                Filtrar
              </Button>
            </div>
          </Column>
        </Row>
      </Card>
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>Ocorreu um erro ...</div>}
      {!isLoading && !error && pagedCompanies.data?.length === 0 && (
        <NoData description={'Não há Clientes para exibir'} />
      )}
      {!isLoading && !error && pagedCompanies.data.length > 0 && (
        <div className="container">
          <DefaultTable
            rows={tableHeader}
            cells={getFilteredCompanies()}
            onClickDelete={onDelete}
            link="companies"
            hideDelete={hideDelete()}
          />
          <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }}>
            <Pagination
              count={pagedCompanies.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
          <ModalConfirmDelete
            description={
              <>
                Tem certeza que deseja excluir <strong>{` ${itemToDelete?.name} `}</strong> como cliente?
              </>
            }
            open={open}
            onClose={onClose}
            onConfirm={onConfirm}
          />
        </div>
      )}
    </Container>
  );
};
